import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useCart } from '@/contexts/CartContext';
import { useMemberAuth } from '@/contexts/MemberAuthContext';
import { InvoiceBuyerType, InvoiceCarrierType, OrderApiError, createOrder } from '@/services/orders';
import { CouponDiscountType, ShippingMethodQuote, fetchShippingMethods, verifyShippingCoupon } from '@/services/shipping';
import { PaymentMethodQuote, fetchPaymentMethods, submitPaymentRedirect } from '@/services/payments';
import { OperationLocation, fetchPublicOperationLocations } from '@/services/operationLocations';
import { readPreferredShippingMethodId, savePreferredShippingMethodId } from '@/services/checkoutPreferences';
import { fetchEcpayStoreMapForm, submitPostForm } from '@/services/logistics';
import { getDistrictsByCity, getPostalCode, taiwanCities } from '@/data/taiwanAddress';
import { withImageFallback } from '@/utils/images';

const inputClass =
  'w-full px-3.5 py-2.5 rounded-xl border border-[#E8E0D6] text-base outline-none focus:ring-2 focus:ring-[#1B3D2A]/15 focus:border-[#1B3D2A]';

export default function Checkout() {
  const navigate = useNavigate();
  const { items, subtotal, clearCart } = useCart();
  const { member, isLoggedIn } = useMemberAuth();

  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [shippingCity, setShippingCity] = useState('');
  const [shippingDistrict, setShippingDistrict] = useState('');
  const [shippingAddressLine, setShippingAddressLine] = useState('');
  const [notes, setNotes] = useState('');
  // 電子發票資料（見後端 InvoiceIssuer 說明）：預設個人發票、無載具（訂單成立後由系統自動開立紙本／
  // 依載具存入電子發票，實際開立時機為付款完成時，不是下單當下）。
  const [invoiceBuyerType, setInvoiceBuyerType] = useState<InvoiceBuyerType>('Personal');
  const [invoiceCarrierType, setInvoiceCarrierType] = useState<InvoiceCarrierType>('None');
  const [invoiceCarrierNumber, setInvoiceCarrierNumber] = useState('');
  const [invoiceBuyerUBN, setInvoiceBuyerUBN] = useState('');
  const [invoiceBuyerName, setInvoiceBuyerName] = useState('');
  const [invoiceLoveCode, setInvoiceLoveCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [shippingMethods, setShippingMethods] = useState<ShippingMethodQuote[]>([]);
  const [shippingMethodId, setShippingMethodId] = useState(() => readPreferredShippingMethodId());
  const [pickupLocations, setPickupLocations] = useState<OperationLocation[]>([]);
  const [pickupLocationId, setPickupLocationId] = useState('');
  const [pickupLocationName, setPickupLocationName] = useState('');
  const [pickupLocationAddress, setPickupLocationAddress] = useState('');
  const [pickupLocationPhone, setPickupLocationPhone] = useState('');
  const [storeMapLoading, setStoreMapLoading] = useState(false);
  const [storeMapError, setStoreMapError] = useState('');
  const [forceManualCvs, setForceManualCvs] = useState(false);
  const [loadingShippingMethods, setLoadingShippingMethods] = useState(true);
  const [shippingMethodsError, setShippingMethodsError] = useState('');

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodQuote[]>([]);
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);
  const [paymentMethodsError, setPaymentMethodsError] = useState('');

  const [couponInput, setCouponInput] = useState('');
  const [couponCode, setCouponCode] = useState(''); // 已成功套用的優惠碼；跟 couponInput（輸入框當下文字）分開，避免使用者打字打到一半就被當作已套用。
  // 優惠碼的折扣類型：FreeShipping 折抵運費（沿用舊有邏輯），FixedAmount／Percentage 折抵商品小計。
  const [couponDiscountType, setCouponDiscountType] = useState<CouponDiscountType | null>(null);
  const [couponPreviewDiscountAmount, setCouponPreviewDiscountAmount] = useState(0);
  const [couponChecking, setCouponChecking] = useState(false);
  const [couponMessage, setCouponMessage] = useState('');
  const [couponMessageTone, setCouponMessageTone] = useState<'success' | 'error'>('success');
  // 訂單送出成功後會清空購物車，若沒有這個旗標，下方「購物車是空的就導回 /cart」的 effect
  // 會在導向 /order-confirmation 的同時被觸發，兩個導航互相搶時序，導致偶爾又被導回購物車頁。
  const orderPlacedRef = useRef(false);

  const shippingDistricts = getDistrictsByCity(shippingCity);
  const shippingPostalCode = getPostalCode(shippingCity, shippingDistrict);

  function handleShippingCityChange(city: string) {
    setShippingCity(city);
    setShippingDistrict('');
  }

  useEffect(() => {
    // 會員已登入時，把註冊/會員中心留存的資料先帶入收件資訊表單，不用每次結帳都重打一次；
    // 用 current || member.X 是為了不要覆蓋使用者已經手動改過的欄位（例如收件人跟會員本人不同時）。
    // 原本這裡漏帶了地址相關欄位（縣市／鄉鎮市區／詳細地址），只帶了姓名/Email/電話，這次一併補上。
    if (isLoggedIn && member) {
      setRecipientName((current) => current || member.Name);
      setRecipientEmail((current) => current || member.Email);
      setRecipientPhone((current) => current || member.Phone || '');
      if (member.AddressCity) {
        setShippingCity((current) => current || member.AddressCity || '');
        setShippingDistrict((current) => current || member.AddressDistrict || '');
      }
      setShippingAddressLine((current) => current || member.AddressLine || '');
    }
  }, [isLoggedIn, member]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('ecpayCvs') !== '1') return;

    const returnedShippingMethodId = params.get('shippingMethodId') || '';
    const storeId = params.get('cvsStoreId') || '';
    const storeName = params.get('cvsStoreName') || '';
    const storeAddress = params.get('cvsAddress') || '';
    const storeTelephone = params.get('cvsTelephone') || '';

    if (returnedShippingMethodId) {
      setShippingMethodId(returnedShippingMethodId);
      savePreferredShippingMethodId(returnedShippingMethodId);
    }
    setPickupLocationPhone(storeId || storeTelephone);
    setPickupLocationName(storeName);
    setPickupLocationAddress(storeAddress);
    setStoreMapError('');
    window.history.replaceState(null, '', window.location.pathname);
  }, []);

  useEffect(() => {
    if (items.length === 0 && !orderPlacedRef.current) {
      navigate('/cart', { replace: true });
    }
  }, [items.length, navigate]);

  // 配送方式的運費會依訂單小計即時變動（例如滿額免運、活動檔期），小計一變就要重新跟後端要一次報價，
  // 不能只在頁面載入時查一次就固定不變。
  useEffect(() => {
    let alive = true;
    setLoadingShippingMethods(true);
    setShippingMethodsError('');
    fetchShippingMethods(subtotal)
      .then((methods) => {
        if (!alive) return;
        setShippingMethods(methods);
        setShippingMethodId((current) => {
          // 保留使用者原本選的方式（如果它在新的報價清單裡還存在），否則預設選第一個。
          if (current && methods.some((method) => method.shippingMethodId === current)) {
            return current;
          }
          return methods[0]?.shippingMethodId || '';
        });
      })
      .catch(() => {
        if (alive) setShippingMethodsError('配送方式載入失敗，請重新整理頁面後再試。');
      })
      .finally(() => {
        if (alive) setLoadingShippingMethods(false);
      });
    return () => {
      alive = false;
    };
  }, [subtotal]);

  useEffect(() => {
    let alive = true;
    fetchPublicOperationLocations().then((countries) => {
      if (!alive) return;
      setPickupLocations(countries.flatMap((country) => country.locations));
    });
    return () => {
      alive = false;
    };
  }, []);

  const selectedShippingMethod = shippingMethods.find((method) => method.shippingMethodId === shippingMethodId) || null;
  const selectedDeliveryType = selectedShippingMethod?.deliveryType || 'HomeDelivery';
  const regularShippingMethods = useMemo(
    () => shippingMethods.filter((method) => method.deliveryType !== 'ConvenienceStorePickup'),
    [shippingMethods]
  );
  const cvsShippingMethods = useMemo(
    () => shippingMethods.filter((method) => method.deliveryType === 'ConvenienceStorePickup'),
    [shippingMethods]
  );
  const cvsBrandCounts = useMemo(
    () => cvsShippingMethods.reduce<Record<string, number>>((counts, method) => ({ ...counts, [method.cvsStoreBrand]: (counts[method.cvsStoreBrand] || 0) + 1 }), {}),
    [cvsShippingMethods]
  );
  const cvsGroupSelected = selectedDeliveryType === 'ConvenienceStorePickup';
  const shouldUseStoreMap = selectedDeliveryType === 'ConvenienceStorePickup' && !!selectedShippingMethod?.requiresStoreMap && !forceManualCvs;
  const shouldUseManualCvs = selectedDeliveryType === 'ConvenienceStorePickup' && !shouldUseStoreMap;
  const selectedPickupLocation = pickupLocations.find((location) => String(location.id) === pickupLocationId) || null;

  function clearCvsPickup() {
    setPickupLocationName('');
    setPickupLocationAddress('');
    setPickupLocationPhone('');
    setStoreMapError('');
  }

  function chooseShippingMethod(methodId: string) {
    const nextMethod = shippingMethods.find((method) => method.shippingMethodId === methodId) || null;
    if (
      selectedShippingMethod?.deliveryType === 'ConvenienceStorePickup' &&
      nextMethod?.deliveryType === 'ConvenienceStorePickup' &&
      selectedShippingMethod.logisticsSubType !== nextMethod.logisticsSubType
    ) {
      clearCvsPickup();
    }
    setShippingMethodId(methodId);
  }

  function chooseCvsGroup() {
    const nextMethodId = cvsGroupSelected ? shippingMethodId : cvsShippingMethods[0]?.shippingMethodId || '';
    if (nextMethodId) chooseShippingMethod(nextMethodId);
  }

  useEffect(() => {
    setForceManualCvs(false);
    setStoreMapError('');
    savePreferredShippingMethodId(shippingMethodId);
  }, [shippingMethodId]);

  useEffect(() => {
    if (!selectedShippingMethod) return;
    if (selectedDeliveryType !== 'StorePickup') setPickupLocationId('');
    if (selectedDeliveryType !== 'ConvenienceStorePickup') {
      setPickupLocationName('');
      setPickupLocationAddress('');
      setPickupLocationPhone('');
    }
  }, [selectedDeliveryType, selectedShippingMethod]);
  // 只有 FreeShipping 類型的優惠碼會讓運費歸零；FixedAmount／Percentage 類型折抵的是商品小計，不影響運費。
  const shippingFee = couponCode && couponDiscountType === 'FreeShipping' ? 0 : (selectedShippingMethod?.fee ?? 0);
  // 折抵金額不會超過商品小計本身（後端也有同一層防呆，這裡只是讓畫面顯示提前一致，避免出現負數金額）。
  const discountAmount = couponCode && couponDiscountType && couponDiscountType !== 'FreeShipping' ? Math.min(couponPreviewDiscountAmount, subtotal) : 0;

  // 付款方式手續費依「折扣後的商品小計＋運費」計算（跟後端 OrderApiController 建立訂單時的計算基準一致），
  // 運費或優惠折扣一變動就要重新跟後端要一次報價，避免畫面顯示金額跟實際送出訂單後的金額兜不起來。
  useEffect(() => {
    let alive = true;
    setLoadingPaymentMethods(true);
    setPaymentMethodsError('');
    fetchPaymentMethods(subtotal - discountAmount + shippingFee)
      .then((methods) => {
        if (!alive) return;
        setPaymentMethods(methods);
        setPaymentMethodId((current) => {
          // 保留使用者原本選的方式（如果它在新的報價清單裡還存在），否則預設選第一個。
          if (current && methods.some((method) => method.paymentMethodId === current)) {
            return current;
          }
          return methods[0]?.paymentMethodId || '';
        });
      })
      .catch(() => {
        if (alive) setPaymentMethodsError('付款方式載入失敗，請重新整理頁面後再試。');
      })
      .finally(() => {
        if (alive) setLoadingPaymentMethods(false);
      });
    return () => {
      alive = false;
    };
  }, [subtotal, discountAmount, shippingFee]);

  const selectedPaymentMethod = paymentMethods.find((method) => method.paymentMethodId === paymentMethodId) || null;
  const paymentExtraFee = selectedPaymentMethod?.fee ?? 0;
  const total = subtotal - discountAmount + shippingFee + paymentExtraFee;

  async function handleApplyCoupon() {
    const code = couponInput.trim();
    if (!code) return;
    setCouponChecking(true);
    setCouponMessage('');
    try {
      const result = await verifyShippingCoupon(code, subtotal);
      if (result.ok) {
        setCouponCode(code);
        setCouponDiscountType(result.discountType);
        setCouponPreviewDiscountAmount(result.discountAmount);
        setCouponMessageTone('success');
      } else {
        setCouponCode('');
        setCouponDiscountType(null);
        setCouponPreviewDiscountAmount(0);
        setCouponMessageTone('error');
      }
      setCouponMessage(result.message);
    } finally {
      setCouponChecking(false);
    }
  }

  function handleRemoveCoupon() {
    setCouponCode('');
    setCouponInput('');
    setCouponDiscountType(null);
    setCouponPreviewDiscountAmount(0);
    setCouponMessage('');
  }

  async function handleOpenStoreMap() {
    if (!shippingMethodId) return;
    setStoreMapLoading(true);
    setStoreMapError('');
    try {
      const form = await fetchEcpayStoreMapForm(shippingMethodId);
      submitPostForm(form.ActionUrl, form.Fields, form.Method);
    } catch (error) {
      const message = error instanceof Error ? error.message : '物流電子地圖開啟失敗，請稍後再試。';
      if (message.includes('尚未啟用') || message.includes('未啟用')) {
        setForceManualCvs(true);
        setStoreMapError('物流電子地圖尚未啟用，已切換為手動輸入門市資料。');
      } else {
        setStoreMapError(message);
      }
    } finally {
      setStoreMapLoading(false);
    }
  }

  const orderItems = useMemo(
    () => items.map((item) => ({ ProductId: item.productId, VariantId: item.variantId, Quantity: item.quantity })),
    [items]
  );

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!shippingMethodId) {
      setFormError('請選擇配送方式。');
      return;
    }
    if (!paymentMethodId) {
      setFormError('請選擇付款方式。');
      return;
    }
    if (selectedDeliveryType === 'StorePickup' && !pickupLocationId) {
      setFormError('請選擇自取門市。');
      setFieldErrors({ PickupLocationId: '請選擇自取門市。' });
      return;
    }
    if (shouldUseManualCvs && (!pickupLocationName.trim() || !pickupLocationAddress.trim())) {
      setFormError('請填寫超商取貨門市資訊。');
      setFieldErrors({
        PickupLocationName: !pickupLocationName.trim() ? '請輸入超商品牌與門市名稱。' : '',
        PickupLocationAddress: !pickupLocationAddress.trim() ? '請輸入超商門市地址。' : '',
      });
      return;
    }
    if (shouldUseStoreMap && !pickupLocationPhone.trim()) {
      setFormError('此配送方式需要超商門市代碼。');
      setFieldErrors({ PickupLocationPhone: '請選擇超商門市或輸入門市代碼。' });
      return;
    }
    setSubmitting(true);
    setFormError('');
    setFieldErrors({});
    try {
      const { order, paymentRedirect } = await createOrder({
        Items: orderItems,
        RecipientName: recipientName.trim(),
        RecipientPhone: recipientPhone.trim(),
        RecipientEmail: recipientEmail.trim(),
        ShippingCity: shippingCity,
        ShippingDistrict: shippingDistrict,
        ShippingPostalCode: shippingPostalCode || undefined,
        ShippingAddressLine: shippingAddressLine.trim(),
        Notes: notes.trim() || undefined,
        PaymentMethodId: paymentMethodId,
        ShippingMethodId: shippingMethodId,
        PickupLocationId: selectedDeliveryType === 'StorePickup' ? pickupLocationId : undefined,
        PickupLocationName: selectedDeliveryType === 'StorePickup' ? selectedPickupLocation?.name : selectedDeliveryType === 'ConvenienceStorePickup' ? pickupLocationName.trim() : undefined,
        PickupLocationAddress: selectedDeliveryType === 'StorePickup'
          ? `${selectedPickupLocation?.addressPostalCode ? `${selectedPickupLocation.addressPostalCode} ` : ''}${selectedPickupLocation?.addressCity || ''}${selectedPickupLocation?.addressDistrict || ''}${selectedPickupLocation?.addressLine || ''}`
          : selectedDeliveryType === 'ConvenienceStorePickup' ? pickupLocationAddress.trim() : undefined,
        PickupLocationPhone: selectedDeliveryType === 'StorePickup' ? selectedPickupLocation?.phone : selectedDeliveryType === 'ConvenienceStorePickup' ? pickupLocationPhone.trim() : undefined,
        ShippingCouponCode: couponCode || undefined,
        InvoiceBuyerType: invoiceBuyerType,
        InvoiceBuyerUBN: invoiceBuyerType === 'Company' ? invoiceBuyerUBN.trim() : undefined,
        InvoiceBuyerName: invoiceBuyerType === 'Company' ? invoiceBuyerName.trim() : undefined,
        InvoiceCarrierType: invoiceBuyerType === 'Personal' ? invoiceCarrierType : undefined,
        InvoiceCarrierNumber: invoiceBuyerType === 'Personal' ? invoiceCarrierNumber.trim() : undefined,
        InvoiceLoveCode: invoiceBuyerType === 'Donation' ? invoiceLoveCode.trim() : undefined,
      });
      orderPlacedRef.current = true;
      clearCart();

      // 選擇線上金流付款方式時，後端會回傳導向金流商付款頁所需的表單資訊，這裡直接把瀏覽器導過去，
      // 不經過訂單完成頁——付款完成或失敗後金流商會依各金流的使用者導回欄位把客人導回 /order-confirmation，
      // 實際的付款狀態則是由 PaymentCallbackController 接收金流商的伺服器回呼後才會真正更新。
      if (paymentRedirect) {
        submitPaymentRedirect(paymentRedirect);
        return;
      }

      // 訪客訂單沒有登入 Session，訂單完成頁若重新用 API 查詢會因為缺少 Email/電話驗證而被拒絕；
      // 這裡直接把剛剛建立好的訂單內容透過導航 state 帶過去，訂單完成頁優先使用它，不需要再打一次 API。
      navigate(`/order-confirmation/${order.OrderNumber}`, { replace: true, state: { order } });
    } catch (err) {
      if (err instanceof OrderApiError) {
        setFormError(err.message);
        setFieldErrors(err.errors || {});
      } else {
        setFormError('訂單建立失敗，請稍後再試。');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (items.length === 0) return null;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-20">
        <div className="border-b border-[#E8E0D6] bg-[#FAF8F5] px-6 py-8 md:px-10 md:py-10 lg:px-16">
          <div className="mx-auto max-w-6xl">
            <nav className="mb-2 flex items-center gap-2 text-sm text-[#9A8A7A]">
              <button type="button" onClick={() => navigate('/cart')} className="hover:text-[#1B3D2A]">購物車</button>
              <i className="ri-arrow-right-s-line" />
              <span className="font-medium text-[#1B3D2A]">結帳</span>
            </nav>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h1 className="text-2xl font-bold text-[#1B3D2A] md:text-3xl">結帳</h1>
              <button
                type="button"
                onClick={() => navigate('/cart')}
                className="flex items-center gap-1.5 rounded-full border border-[#1B3D2A] px-4 py-2 text-sm font-bold text-[#1B3D2A] hover:bg-[#1B3D2A] hover:text-white"
              >
                <i className="ri-arrow-left-line" />
                返回購物車
              </button>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-6xl px-6 py-8 md:px-10 md:py-12 lg:px-16">
          {!isLoggedIn && (
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#E8E0D6] bg-[#FAF8F5] px-5 py-4">
              <p className="text-sm text-[#5A6B5A]">
                <i className="ri-information-line mr-1 text-[#D4A574]" />
                登入會員可保留訂購資訊、方便日後查詢訂單，也可以直接以訪客身份結帳。
              </p>
              <div className="flex gap-2">
                <button type="button" onClick={() => navigate('/login?redirect=/checkout')} className="rounded-full border border-[#1B3D2A] px-4 py-2 text-sm font-bold text-[#1B3D2A] hover:bg-[#1B3D2A] hover:text-white">
                  會員登入
                </button>
                <button type="button" onClick={() => navigate('/register?redirect=/checkout')} className="rounded-full bg-[#1B3D2A] px-4 py-2 text-sm font-bold text-white hover:bg-[#143026]">
                  加入會員
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="grid gap-8 lg:grid-cols-3 lg:items-start">
            <div className="lg:col-span-2 space-y-6">
              <div className="rounded-2xl border border-[#E8E0D6] bg-white p-6">
                <h2 className="mb-4 text-base font-bold text-[#1B3D2A]">收件資訊</h2>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label htmlFor="checkout-name" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">收件人姓名</label>
                    <input id="checkout-name" required value={recipientName} onChange={(e) => setRecipientName(e.target.value)} className={inputClass} />
                    {fieldErrors.RecipientName && <p className="mt-1 text-xs text-[#c3423f]">{fieldErrors.RecipientName}</p>}
                  </div>
                  <div>
                    <label htmlFor="checkout-phone" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">聯絡電話</label>
                    <input id="checkout-phone" required value={recipientPhone} onChange={(e) => setRecipientPhone(e.target.value)} placeholder="0912345678" className={inputClass} />
                    {fieldErrors.RecipientPhone && <p className="mt-1 text-xs text-[#c3423f]">{fieldErrors.RecipientPhone}</p>}
                  </div>
                  <div className="md:col-span-2">
                    <label htmlFor="checkout-email" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">Email</label>
                    <input id="checkout-email" type="email" required value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} placeholder="name@example.com" className={inputClass} />
                    <p className="mt-1 text-xs text-[#9A8A7A]">訂單狀態通知與訪客訂單查詢皆會使用此 Email。</p>
                    {fieldErrors.RecipientEmail && <p className="mt-1 text-xs text-[#c3423f]">{fieldErrors.RecipientEmail}</p>}
                  </div>
                  {selectedDeliveryType === 'HomeDelivery' && (
                    <>
                      <div>
                        <label htmlFor="checkout-city" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">縣市</label>
                        <select id="checkout-city" required value={shippingCity} onChange={(e) => handleShippingCityChange(e.target.value)} className={`${inputClass} bg-white`}>
                          <option value="">請選擇縣市</option>
                          {taiwanCities.map((city) => <option key={city} value={city}>{city}</option>)}
                        </select>
                        {fieldErrors.ShippingCity && <p className="mt-1 text-xs text-[#c3423f]">{fieldErrors.ShippingCity}</p>}
                      </div>
                      <div>
                        <label htmlFor="checkout-district" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">鄉鎮市區</label>
                        <select
                          id="checkout-district"
                          required
                          value={shippingDistrict}
                          onChange={(e) => setShippingDistrict(e.target.value)}
                          className={`${inputClass} bg-white`}
                          disabled={!shippingCity}
                        >
                          <option value="">{shippingCity ? '請選擇鄉鎮市區' : '請先選擇縣市'}</option>
                          {shippingDistricts.map((district) => <option key={district.name} value={district.name}>{district.name}</option>)}
                        </select>
                        {fieldErrors.ShippingDistrict && <p className="mt-1 text-xs text-[#c3423f]">{fieldErrors.ShippingDistrict}</p>}
                      </div>
                      <div>
                        <label htmlFor="checkout-postal-code" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">郵遞區號</label>
                        <input id="checkout-postal-code" value={shippingPostalCode} readOnly placeholder="選擇鄉鎮市區後自動帶出" className={`${inputClass} bg-[#F1EEE8] text-[#9A8A7A]`} />
                      </div>
                      <div className="md:col-span-2">
                        <label htmlFor="checkout-address" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">詳細地址</label>
                        <input id="checkout-address" required value={shippingAddressLine} onChange={(e) => setShippingAddressLine(e.target.value)} placeholder="街道、門牌號碼、樓層" className={inputClass} />
                        {fieldErrors.ShippingAddressLine && <p className="mt-1 text-xs text-[#c3423f]">{fieldErrors.ShippingAddressLine}</p>}
                      </div>
                    </>
                  )}
                  <div className="md:col-span-2">
                    <label htmlFor="checkout-notes" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">備註（選填）</label>
                    <textarea id="checkout-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="配送時間、特殊需求等" className={`${inputClass} resize-none`} />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-[#E8E0D6] bg-white p-6">
                <h2 className="mb-4 text-base font-bold text-[#1B3D2A]">配送方式</h2>
                {loadingShippingMethods ? (
                  <p className="text-sm text-[#9A8A7A]">載入配送方式中...</p>
                ) : shippingMethodsError ? (
                  <p className="text-sm text-[#c3423f]">{shippingMethodsError}</p>
                ) : shippingMethods.length === 0 ? (
                  <p className="text-sm text-[#c3423f]">目前沒有可用的配送方式，請直接聯繫客服協助下單。</p>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {regularShippingMethods.map((method) => {
                      const checked = shippingMethodId === method.shippingMethodId;
                      return (
                        <label
                          key={method.shippingMethodId}
                          className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition ${
                            checked ? 'border-[#1B3D2A] bg-[#1B3D2A]/5' : 'border-[#E8E0D6] hover:border-[#1B3D2A]/40'
                          }`}
                        >
                          <input type="radio" name="shipping-method" className="sr-only" checked={checked} onChange={() => chooseShippingMethod(method.shippingMethodId)} />
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center justify-between gap-2">
                              <span className="block text-sm font-bold text-[#1B3D2A]">{method.name}</span>
                              <span className="shrink-0 text-sm font-bold text-[#1B3D2A]">{method.fee > 0 ? `NT$ ${method.fee.toLocaleString()}` : '免運費'}</span>
                            </span>
                            {method.description && <span className="mt-0.5 block text-xs text-[#9A8A7A]">{method.description}</span>}
                            {method.appliedRuleName && <span className="mt-1 inline-flex rounded-full bg-[#D4A574]/20 px-2 py-0.5 text-[11px] font-bold text-[#9A6B2F]">{method.appliedRuleName}</span>}
                          </span>
                        </label>
                      );
                    })}
                    {cvsShippingMethods.length > 0 && (
                      <div
                        className={`rounded-xl border p-4 transition sm:col-span-2 ${
                          cvsGroupSelected ? 'border-[#1B3D2A] bg-[#1B3D2A]/5' : 'border-[#E8E0D6] hover:border-[#1B3D2A]/40'
                        }`}
                      >
                        <label className="block cursor-pointer">
                          <input type="radio" name="shipping-method" className="sr-only" checked={cvsGroupSelected} onChange={chooseCvsGroup} />
                          <span className="flex items-start justify-between gap-2">
                            <span>
                              <span className="block text-sm font-bold text-[#1B3D2A]">超商取貨</span>
                              <span className="mt-0.5 block text-xs text-[#9A8A7A]">選擇通路後再使用電子地圖或手動填寫門市資料。</span>
                            </span>
                            <span className="shrink-0 text-sm font-bold text-[#1B3D2A]">{cvsGroupSelected && selectedShippingMethod ? (selectedShippingMethod.fee > 0 ? `NT$ ${selectedShippingMethod.fee.toLocaleString()}` : '免運') : '選擇後計算'}</span>
                          </span>
                        </label>
                        {cvsGroupSelected && (
                          <div className="mt-4 grid gap-2 sm:grid-cols-4">
                            {cvsShippingMethods.map((method) => {
                              const checked = shippingMethodId === method.shippingMethodId;
                              return (
                                <button
                                  key={method.shippingMethodId}
                                  type="button"
                                  onClick={() => chooseShippingMethod(method.shippingMethodId)}
                                  className={`min-h-20 rounded-xl border px-3 py-2 text-left transition ${
                                    checked ? 'border-[#1B3D2A] bg-white text-[#1B3D2A] ring-2 ring-[#1B3D2A]/10' : 'border-[#E8E0D6] bg-white text-[#5A6B5A] hover:border-[#1B3D2A]/40'
                                  }`}
                                >
                                  <span className="block text-sm font-bold">{cvsBrandCounts[method.cvsStoreBrand] > 1 ? method.name : method.cvsStoreBrandLabel}</span>
                                  <span className="mt-1 block text-[11px] text-[#9A8A7A]">{method.logisticsSubTypeLabel || method.cvsStoreBrandLabel}</span>
                                  <span className="mt-1 block text-xs text-[#9A8A7A]">{method.fee > 0 ? `NT$ ${method.fee.toLocaleString()}` : '免運'}</span>
                                  <span className="mt-1 block text-[11px] text-[#9A6B2F]">{method.requiresStoreMap ? '電子地圖選店' : '手動門市資料'}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {selectedDeliveryType === 'StorePickup' && (
                  <div className="mt-4 rounded-xl border border-[#E8E0D6] bg-[#FAF8F5] p-4">
                    <label htmlFor="checkout-pickup-location" className="block text-sm font-bold text-[#1B3D2A] mb-1.5">選擇自取門市</label>
                    <select id="checkout-pickup-location" required value={pickupLocationId} onChange={(e) => setPickupLocationId(e.target.value)} className={`${inputClass} bg-white`}>
                      <option value="">請選擇門市</option>
                      {pickupLocations.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name}｜{location.addressCity}{location.addressDistrict}{location.addressLine}
                        </option>
                      ))}
                    </select>
                    {selectedPickupLocation && (
                      <p className="mt-2 text-xs leading-5 text-[#5A6B5A]">
                        {selectedPickupLocation.addressPostalCode ? `${selectedPickupLocation.addressPostalCode} ` : ''}
                        {selectedPickupLocation.addressCity}{selectedPickupLocation.addressDistrict}{selectedPickupLocation.addressLine}
                        {selectedPickupLocation.phone ? `｜${selectedPickupLocation.phone}` : ''}
                      </p>
                    )}
                    {fieldErrors.PickupLocationId && <p className="mt-1 text-xs text-[#c3423f]">{fieldErrors.PickupLocationId}</p>}
                  </div>
                )}
                {shouldUseStoreMap && (
                  <div className="mt-4 rounded-xl border border-[#E8E0D6] bg-[#FAF8F5] p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm font-bold text-[#1B3D2A]">超商電子地圖選店</p>
                        <p className="mt-1 text-xs leading-5 text-[#9A8A7A]">請透過綠界電子地圖選擇取貨門市，系統會自動帶回門市代碼、名稱與地址。</p>
                      </div>
                      <button
                        type="button"
                        onClick={handleOpenStoreMap}
                        disabled={storeMapLoading}
                        className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl bg-[#1B3D2A] px-5 text-sm font-bold text-white hover:bg-[#143026] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <i className="ri-map-pin-line" />
                        {storeMapLoading ? '開啟中' : '選擇超商門市'}
                      </button>
                    </div>
                    {pickupLocationPhone && pickupLocationName && pickupLocationAddress && (
                      <div className="mt-3 rounded-xl border border-[#D9CDBE] bg-white px-4 py-3 text-sm text-[#5A6B5A]">
                        <p className="font-bold text-[#1B3D2A]">{pickupLocationName}（{pickupLocationPhone}）</p>
                        <p className="mt-1">{pickupLocationAddress}</p>
                      </div>
                    )}
                    {storeMapError && <p className="mt-2 text-xs text-[#c3423f]">{storeMapError}</p>}
                    {fieldErrors.PickupLocationPhone && <p className="mt-2 text-xs text-[#c3423f]">{fieldErrors.PickupLocationPhone}</p>}
                  </div>
                )}
                {shouldUseManualCvs && storeMapError && (
                  <p className="mt-3 rounded-xl border border-[#E8E0D6] bg-[#FAF8F5] px-4 py-3 text-xs text-[#9A6B2F]">{storeMapError}</p>
                )}
                {shouldUseManualCvs && (
                  <div className="mt-4 grid gap-3 rounded-xl border border-[#E8E0D6] bg-[#FAF8F5] p-4 md:grid-cols-2">
                    <p className="text-xs leading-5 text-[#9A6B2F] md:col-span-2">
                      目前尚未串接超商電子地圖，請填入欲取貨的超商品牌、門市名稱或代號與地址，客服會依資料人工確認。
                    </p>
                    <div>
                      <label htmlFor="checkout-cvs-name" className="block text-sm font-bold text-[#1B3D2A] mb-1.5">超商品牌／門市名稱</label>
                      <input id="checkout-cvs-name" required value={pickupLocationName} onChange={(e) => setPickupLocationName(e.target.value)} placeholder="例：7-11 松高門市" className={inputClass} />
                      {fieldErrors.PickupLocationName && <p className="mt-1 text-xs text-[#c3423f]">{fieldErrors.PickupLocationName}</p>}
                    </div>
                    <div>
                      <label htmlFor="checkout-cvs-phone" className="block text-sm font-bold text-[#1B3D2A] mb-1.5">門市代號／電話（選填）</label>
                      <input id="checkout-cvs-phone" value={pickupLocationPhone} onChange={(e) => setPickupLocationPhone(e.target.value)} placeholder="例：123456 或門市電話" className={inputClass} />
                    </div>
                    <div className="md:col-span-2">
                      <label htmlFor="checkout-cvs-address" className="block text-sm font-bold text-[#1B3D2A] mb-1.5">超商門市地址</label>
                      <input id="checkout-cvs-address" required value={pickupLocationAddress} onChange={(e) => setPickupLocationAddress(e.target.value)} placeholder="請輸入完整門市地址" className={inputClass} />
                      {fieldErrors.PickupLocationAddress && <p className="mt-1 text-xs text-[#c3423f]">{fieldErrors.PickupLocationAddress}</p>}
                    </div>
                  </div>
                )}
                {fieldErrors.ShippingMethodId && <p className="mt-2 text-xs text-[#c3423f]">{fieldErrors.ShippingMethodId}</p>}
              </div>

              <div className="rounded-2xl border border-[#E8E0D6] bg-white p-6">
                <h2 className="mb-4 text-base font-bold text-[#1B3D2A]">付款方式</h2>
                {loadingPaymentMethods ? (
                  <p className="text-sm text-[#9A8A7A]">載入付款方式中...</p>
                ) : paymentMethodsError ? (
                  <p className="text-sm text-[#c3423f]">{paymentMethodsError}</p>
                ) : paymentMethods.length === 0 ? (
                  <p className="text-sm text-[#c3423f]">目前沒有可用的付款方式，請直接聯繫客服協助下單。</p>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {paymentMethods.map((method) => {
                      const checked = paymentMethodId === method.paymentMethodId;
                      return (
                        <label
                          key={method.paymentMethodId}
                          className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition ${
                            checked ? 'border-[#1B3D2A] bg-[#1B3D2A]/5' : 'border-[#E8E0D6] hover:border-[#1B3D2A]/40'
                          }`}
                        >
                          <input type="radio" name="payment-method" className="sr-only" checked={checked} onChange={() => setPaymentMethodId(method.paymentMethodId)} />
                          <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${checked ? 'bg-[#1B3D2A] text-white' : 'bg-[#FAF8F5] text-[#5A6B5A]'}`}>
                            <i className={method.settlementType === 'Gateway' ? 'ri-bank-card-line' : 'ri-truck-line'} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center justify-between gap-2">
                              <span className="block text-sm font-bold text-[#1B3D2A]">{method.name}</span>
                              {method.fee > 0 && <span className="shrink-0 text-xs font-bold text-[#9A6B2F]">+NT$ {method.fee.toLocaleString()}</span>}
                            </span>
                            {method.description && <span className="mt-0.5 block text-xs text-[#9A8A7A]">{method.description}</span>}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
                {fieldErrors.PaymentMethodId && <p className="mt-2 text-xs text-[#c3423f]">{fieldErrors.PaymentMethodId}</p>}
              </div>

              <div className="rounded-2xl border border-[#E8E0D6] bg-white p-6">
                <h2 className="mb-1 text-base font-bold text-[#1B3D2A]">電子發票</h2>
                <p className="mb-4 text-xs text-[#9A8A7A]">依法規需開立電子發票，付款完成後系統會自動開立並依您的選擇寄送或存入載具。</p>
                <div className="grid gap-2 sm:grid-cols-3">
                  {(
                    [
                      { value: 'Personal' as InvoiceBuyerType, label: '個人發票' },
                      { value: 'Company' as InvoiceBuyerType, label: '統一編號發票' },
                      { value: 'Donation' as InvoiceBuyerType, label: '捐贈發票' },
                    ]
                  ).map((option) => (
                    <label
                      key={option.value}
                      className={`flex cursor-pointer items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-bold transition ${
                        invoiceBuyerType === option.value ? 'border-[#1B3D2A] bg-[#1B3D2A]/5 text-[#1B3D2A]' : 'border-[#E8E0D6] text-[#5A6B5A] hover:border-[#1B3D2A]/40'
                      }`}
                    >
                      <input type="radio" name="invoice-buyer-type" className="sr-only" checked={invoiceBuyerType === option.value} onChange={() => setInvoiceBuyerType(option.value)} />
                      {option.label}
                    </label>
                  ))}
                </div>

                {invoiceBuyerType === 'Personal' && (
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <div>
                      <label htmlFor="checkout-invoice-carrier-type" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">發票載具</label>
                      <select
                        id="checkout-invoice-carrier-type"
                        value={invoiceCarrierType}
                        onChange={(e) => setInvoiceCarrierType(e.target.value as InvoiceCarrierType)}
                        className={`${inputClass} bg-white`}
                      >
                        <option value="None">索取紙本發票</option>
                        <option value="Member">會員載具（以本次訂購 Email 存放）</option>
                        <option value="MobileBarcode">手機條碼載具</option>
                        <option value="CitizenCertificate">自然人憑證載具</option>
                      </select>
                    </div>
                    {(invoiceCarrierType === 'MobileBarcode' || invoiceCarrierType === 'CitizenCertificate') && (
                      <div>
                        <label htmlFor="checkout-invoice-carrier-number" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">
                          {invoiceCarrierType === 'MobileBarcode' ? '手機條碼（/開頭共 8 碼）' : '自然人憑證條碼（16 碼）'}
                        </label>
                        <input
                          id="checkout-invoice-carrier-number"
                          value={invoiceCarrierNumber}
                          onChange={(e) => setInvoiceCarrierNumber(e.target.value)}
                          placeholder={invoiceCarrierType === 'MobileBarcode' ? '/AB12345' : 'AB12345678901234'}
                          className={inputClass}
                        />
                        {fieldErrors.InvoiceCarrierNumber && <p className="mt-1 text-xs text-[#c3423f]">{fieldErrors.InvoiceCarrierNumber}</p>}
                      </div>
                    )}
                  </div>
                )}

                {invoiceBuyerType === 'Company' && (
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <div>
                      <label htmlFor="checkout-invoice-ubn" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">統一編號</label>
                      <input id="checkout-invoice-ubn" value={invoiceBuyerUBN} onChange={(e) => setInvoiceBuyerUBN(e.target.value)} placeholder="8 碼數字" className={inputClass} />
                      {fieldErrors.InvoiceBuyerUBN && <p className="mt-1 text-xs text-[#c3423f]">{fieldErrors.InvoiceBuyerUBN}</p>}
                    </div>
                    <div>
                      <label htmlFor="checkout-invoice-company-name" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">發票抬頭</label>
                      <input id="checkout-invoice-company-name" value={invoiceBuyerName} onChange={(e) => setInvoiceBuyerName(e.target.value)} placeholder="公司名稱" className={inputClass} />
                      {fieldErrors.InvoiceBuyerName && <p className="mt-1 text-xs text-[#c3423f]">{fieldErrors.InvoiceBuyerName}</p>}
                    </div>
                  </div>
                )}

                {invoiceBuyerType === 'Donation' && (
                  <div className="mt-4">
                    <label htmlFor="checkout-invoice-love-code" className="block text-sm font-medium text-[#1B3D2A] mb-1.5">捐贈碼</label>
                    <input id="checkout-invoice-love-code" value={invoiceLoveCode} onChange={(e) => setInvoiceLoveCode(e.target.value)} placeholder="3~7 碼數字" className={inputClass} />
                    {fieldErrors.InvoiceLoveCode && <p className="mt-1 text-xs text-[#c3423f]">{fieldErrors.InvoiceLoveCode}</p>}
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-2xl sticky top-20 z-10 border border-[#E8E0D6] bg-[#FAF8F5] p-6 md:top-24">
              <h2 className="text-base font-bold text-[#1B3D2A]">訂單摘要</h2>
              <div className="mt-4 max-h-64 space-y-3 overflow-y-auto pr-1">
                {items.map((item) => (
                  <div key={item.key} className="flex items-center gap-3">
                    <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-white">
                      <img src={withImageFallback(item.image)} alt={item.name} className="h-full w-full object-cover object-top" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold text-[#1B3D2A]">{item.name}</p>
                      {item.variantLabel && <p className="text-xs text-[#9A8A7A]">{item.variantLabel} × {item.quantity}</p>}
                      {!item.variantLabel && <p className="text-xs text-[#9A8A7A]">× {item.quantity}</p>}
                    </div>
                    <p className="shrink-0 text-sm font-bold text-[#1B3D2A]">NT$ {(item.unitPrice * item.quantity).toLocaleString()}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 space-y-1.5 border-t border-[#E8E0D6] pt-4 text-sm text-[#5A6B5A]">
                <div className="flex items-center justify-between">
                  <span>商品小計</span>
                  <span>NT$ {subtotal.toLocaleString()}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex items-center justify-between">
                    <span>優惠折扣{couponCode && <span className="ml-1 text-xs text-[#5A6B5A]">（{couponCode}）</span>}</span>
                    <span>-NT$ {discountAmount.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span>運費{couponCode && couponDiscountType === 'FreeShipping' && <span className="ml-1 text-xs text-[#5A6B5A]">（已套用優惠碼）</span>}</span>
                  <span>{shippingFee > 0 ? `NT$ ${shippingFee.toLocaleString()}` : '免運費'}</span>
                </div>
                {paymentExtraFee > 0 && (
                  <div className="flex items-center justify-between">
                    <span>付款手續費</span>
                    <span>NT$ {paymentExtraFee.toLocaleString()}</span>
                  </div>
                )}
              </div>

              <div className="mt-3 border-t border-[#E8E0D6] pt-3">
                {couponCode ? (
                  <div className="flex items-center justify-between gap-2 rounded-xl bg-[#1B3D2A]/5 px-3 py-2">
                    <span className="text-sm font-bold text-[#1B3D2A]">
                      <i className="ri-coupon-3-line mr-1" />
                      {couponCode}
                    </span>
                    <button type="button" onClick={handleRemoveCoupon} className="text-xs font-bold text-[#9A8A7A] hover:text-[#c3423f]">
                      移除
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value)}
                      placeholder="優惠碼（選填）"
                      className="min-w-0 flex-1 rounded-xl border border-[#E8E0D6] px-3 py-2 text-sm outline-none focus:border-[#1B3D2A] focus:ring-2 focus:ring-[#1B3D2A]/15"
                    />
                    <button
                      type="button"
                      disabled={couponChecking || !couponInput.trim()}
                      onClick={handleApplyCoupon}
                      className="shrink-0 rounded-xl border border-[#1B3D2A] px-4 text-sm font-bold text-[#1B3D2A] hover:bg-[#1B3D2A] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {couponChecking ? '驗證中' : '套用'}
                    </button>
                  </div>
                )}
                {couponMessage && (
                  <p className={`mt-1.5 text-xs ${couponMessageTone === 'success' ? 'text-[#1B3D2A]' : 'text-[#c3423f]'}`}>{couponMessage}</p>
                )}
              </div>

              <div className="mt-3 flex items-center justify-between border-t border-[#E8E0D6] pt-3">
                <span className="font-bold text-[#1B3D2A]">總計</span>
                <span className="text-xl font-bold text-[#1B3D2A]">NT$ {total.toLocaleString()}</span>
              </div>

              {formError && <div className="mt-4 rounded-xl bg-[#fdebea] px-4 py-3 text-sm text-[#c3423f]">{formError}</div>}

              <button
                type="submit"
                disabled={submitting || loadingShippingMethods || !shippingMethodId || loadingPaymentMethods || !paymentMethodId}
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-[#1B3D2A] py-3.5 text-sm font-bold text-white hover:bg-[#143026] disabled:opacity-60"
              >
                <i className="ri-secure-payment-line" />
                {submitting ? '送出中...' : '送出訂單'}
              </button>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}
